import type { DeviceDTO } from './types'

/**
 * Library and palette search. A device matches on its name, make, model or
 * IP (the "base" text, as before) — or, failing that, on the name or connector
 * of one of its ports, in which case the hit carries a hint so the row can
 * say why it appeared ("matches: Timecode In · BNC (SDI)").
 */

export type SearchEntry = {
  base: string
  ports: Array<{ text: string; label: string }>
}

export type SearchIndex = Map<string, SearchEntry>

export function indexDevice(device: {
  name: string
  manufacturer: string
  model: string
  ipAddress?: string | null
  ports: { name: string; connector: string }[]
}): SearchEntry {
  const base = [device.name, device.manufacturer, device.model, device.ipAddress ?? '']
    .join(' ')
    .toLowerCase()
  const seen = new Set<string>()
  const ports: SearchEntry['ports'] = []
  for (const port of device.ports) {
    const label = `${port.name} · ${port.connector}`
    if (seen.has(label)) continue
    seen.add(label)
    ports.push({ text: label.toLowerCase(), label })
  }
  return { base, ports }
}

/** Built once per device list and memoised by the caller. */
export function buildSearchIndex(devices: DeviceDTO[]): SearchIndex {
  return new Map(devices.map((device) => [device.id, indexDevice(device)]))
}

/**
 * The palette gets its entries precomputed on the server, because the slim
 * payload it ships with does not carry ports: searching by connector ("BNC",
 * "XLR") is a real way people find gear, and dropping a megabyte of port rows
 * should not cost them that.
 */
export function searchIndexFrom(devices: { id: string; search: SearchEntry }[]): SearchIndex {
  return new Map(devices.map((device) => [device.id, device.search]))
}

export type SearchHit<T> = { device: T; hint: string | null }

/**
 * Same substring semantics as the old `includes` filter for the base text, so
 * every needle that used to match exactly one row still does; port matches
 * only ever add rows that the base text did not already match.
 */
export function searchDevices<T extends { id: string }>(
  pool: T[],
  index: SearchIndex,
  query: string,
): SearchHit<T>[] {
  const needle = query.trim().toLowerCase()
  if (!needle) return pool.map((device) => ({ device, hint: null }))
  const hits: SearchHit<T>[] = []
  for (const device of pool) {
    const entry = index.get(device.id)
    if (!entry) continue
    if (entry.base.includes(needle)) {
      hits.push({ device, hint: null })
      continue
    }
    const port = entry.ports.find((candidate) => candidate.text.includes(needle))
    if (port) hits.push({ device, hint: port.label })
  }
  return hits
}
